import React, { useState, useCallback, memo, useEffect, useRef } from 'react';
import ReactFlow, {
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    addEdge,
    Panel,
    Handle,
    Position,
    useReactFlow,
    ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import './Workflow.css';
import { v4 as uuidv4 } from 'uuid';

import { useDispatch, useSelector } from 'react-redux';
import { getFlowchartsNew, createorupdateFlowchart } from '../../store/actions/flowchartActions';
import { openOverlay, closeOverlay } from '../../store/actions/riskActions';
import { useHistory } from 'react-router-dom';

// Custom node components for editable text
const EditableTextNode = memo(({ data, isConnectable }) => {
    return (
        <>
            <Handle
                type="target"
                position={Position.Left}
                isConnectable={isConnectable}
            />
            <Handle
                type="source"
                position={Position.Right}
                isConnectable={isConnectable}
            />
            <div
                className="text-node"
                onDoubleClick={() => data.setIsEditing(true)}
            >
                {data.isEditing ? (
                    <input
                        className="text-node-input"
                        type="text"
                        value={data.text}
                        onChange={(e) => data.onChange(e.target.value)}
                        autoFocus
                        onBlur={() => data.setIsEditing(false)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                data.setIsEditing(false);
                            }
                        }}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            width: '100%',
                            outline: 'none'
                        }}
                    />
                ) : (
                    <div style={{ cursor: 'pointer' }}>{data.text}</div>
                )}
            </div>
        </>
    );
});

const EditableVerticalTextNode = memo(({ data, isConnectable }) => {
    return (
        <div
            className="vertical-text-node"
            onDoubleClick={() => data.setIsEditing(true)}
            style={{
                background: 'white',
                border: '1px solid black',
                padding: '10px 2px',
                margin: 0,
                boxSizing: 'border-box'
            }}
        >
            {data.isEditing ? (
                <input
                    className="vertical-text-input"
                    type="text"
                    value={data.text}
                    onChange={(e) => data.onChange(e.target.value)}
                    autoFocus
                    onBlur={() => data.setIsEditing(false)}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            data.setIsEditing(false);
                        }
                    }}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        fontSize: '12px',
                        writingMode: 'vertical-lr',
                        textOrientation: 'upright',
                        letterSpacing: '3px',
                        width: '100%',
                        height: '100%',
                        outline: 'none',
                        textAlign: 'center',
                        padding: 0,
                        margin: 0
                    }}
                />
            ) : (
                <div style={{
                    cursor: 'pointer',
                    margin: 0,
                    padding: 0
                }}>
                    {data.text}
                </div>
            )}
        </div>
    );
});

// Add custom Rhombus node
const RhombusNode = memo(({ data, isConnectable }) => {
    return (
        <div className="custom-rhombus-wrapper">
            <div className="rhombus-container">
                <div className="rhombus"></div>
                <div className="rhombus-content" onDoubleClick={() => data.setIsEditing(true)}>
                    {data.isEditing ? (
                        <textarea
                            value={data.text}
                            onChange={(e) => data.onChange(e.target.value)}
                            autoFocus
                            onBlur={() => data.setIsEditing(false)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && e.shiftKey) {
                                    e.preventDefault();
                                    data.setIsEditing(false);
                                }
                            }}
                            className="rhombus-textarea"
                        />
                    ) : (
                        <div className="rhombus-text">
                            {data.text}
                        </div>
                    )}
                </div>
                <Handle
                    type="target"
                    position={Position.Top}
                    isConnectable={isConnectable}
                />
                <Handle
                    type="source"
                    position={Position.Right}
                    isConnectable={isConnectable}
                />
                <Handle
                    type="source"
                    position={Position.Bottom}
                    isConnectable={isConnectable}
                />
                <Handle
                    type="target"
                    position={Position.Left}
                    isConnectable={isConnectable}
                />
            </div>
        </div>
    );
});

// Custom Default Node with handles on all sides
const DefaultNode = memo(({ data, isConnectable }) => {
    return (
        <div style={{ position: 'relative' }}>
            {/* Render all handles */}
            {data.handles?.map((handle, index) => (
                <Handle
                    key={`${handle.position}-${handle.type}-${index}`}
                    type={handle.type}
                    position={handle.position}
                    isConnectable={isConnectable}
                    style={{ background: 'black' }}
                />
            ))}
            <div style={{ padding: '10px' }}>
                {data.label}
            </div>
        </div>
    );
});

// Custom node components for Risk and Asset
const RiskNode = memo(({ data }) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const handleContextMenu = (event) => {
        event.preventDefault(); // Prevent default context menu
        
        // First close any existing overlay
        dispatch(closeOverlay());
        
        // Access the functions from data
        if (data.setActiveRiskForWorkflow) {
            data.setActiveRiskForWorkflow(data.id);
        }
        
        if (data.setMenuItem) {
            data.setMenuItem('risk');
        }
    };

    return (
        <div
            className="system-node risk-node"
            onContextMenu={handleContextMenu}
        >
            <div className="system-header" style={{ background: '#e74c3c' }}>⚠️ Risk</div>
            <div className="system-content">
                <strong>{data.name}</strong><br />
                ID: {data.id}<br />
                {data.definition}
            </div>
            <Handle
                type="target"
                position={Position.Left}
                isConnectable={true}
            />
            <Handle
                type="source"
                position={Position.Right}
                isConnectable={true}
            />
        </div>
    );
});

const AssetNode = memo(({ data }) => {
 
    
    const handleContextMenu = (event) => {
        event.preventDefault(); // Prevent default context menu
        //alert('clicked');
        if (data.setMenuItem) {
            //console.log('setting menu item to asset');
            data.setMenuItem('inventory');
        }
        if (data.setActiveAssetForWorkflow) {
            data.setActiveAssetForWorkflow(data.id);
        }
    };

    return (
        <div
            className="system-node asset-node"
            onContextMenu={handleContextMenu}
        >
            <div className="system-header" style={{ background: '#27ae60' }}>💎 Asset</div>
            <div className="system-content">
                <strong>{data.name}</strong><br />
                ID: {data.id}<br />
                {data.description}
            </div>
            <Handle
                type="target"
                position={Position.Left}
                isConnectable={true}
            />
            <Handle
                type="source"
                position={Position.Right}
                isConnectable={true}
            />
        </div>
    );
});

// Define custom node types
const customNodeTypes = {
    editableText: EditableTextNode,
    editableVerticalText: EditableVerticalTextNode,
    rhombusNode: RhombusNode,
    riskNode: RiskNode,
    assetNode: AssetNode
};

const nodeTypes = {

    risk: {
        label: 'Risk',
        className: 'system-node',
        content: 'Select Risk'
    },
    asset: {
        label: 'Asset',
        className: 'system-node',
        content: 'Select Asset'
    },
    rhombus: {
        label: 'Decision',
        className: 'rhombus-container',
        content: 'Does\ndocument\nproper?'
    },
    yes: {
        label: 'Yes',
        className: 'yes-node',
        content: 'YES'
    },
    no: {
        label: 'No',
        className: 'no-node',
        content: 'NO'
    },
    end: {
        label: 'End',
        className: 'end-node',
        content: 'END'
    },
    verticalText: {
        label: 'Vertical Text',
        className: 'vertical-text-node',
        content: 'PROCESS EXPLAINER'
    },
    text: {
        label: 'Text',
        className: 'text-node',
        content: 'TEXT'
    }
};

function NodePalette({ setNodes, setMenuItem, setActiveRiskForWorkflow, setActiveAssetForWorkflow }) {

    //lets get the risks and assets from the store
    const risks = useSelector((state) => state.workflows.risks);
    const assets = useSelector((state) => state.workflows.assets);

    const [showRiskSelect, setShowRiskSelect] = useState(false);
    const [showAssetSelect, setShowAssetSelect] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState({ x: 0, y: 0 });
    const [searchRisk, setSearchRisk] = useState('');
    const [searchAsset, setSearchAsset] = useState('');
    const riskDropdownRef = useRef(null);
    const assetDropdownRef = useRef(null);
    const riskButtonRef = useRef(null);
    const assetButtonRef = useRef(null);

    // Move risks and assets data inside the component
    const risks2 = [
        {
            id: 'risk1',
            name: 'Risk 1',
            description: 'Risk 1 description'
        },
        {
            id: 'risk2',
            name: 'Risk 2',
            description: 'Risk 2 description'
        },
        {
            id: 'risk3',
            name: 'Another Risk',
            description: 'Another risk description'
        },
        {
            id: 'risk4',
            name: 'Security Risk',
            description: 'Security risk description'
        },
        {
            id: 'risk5',
            name: 'Performance Risk',
            description: 'Performance risk description'
        },
        {
            id: 'risk6',
            name: 'Financial Risk',
            description: 'Financial risk description'
        }
    ];

    const assets2 = [
        {
            id: 'asset1',
            name: 'Asset 1',
            description: 'Asset 1 description'
        },
        {
            id: 'asset2',
            name: 'Asset 2',
            description: 'Asset 2 description'
        },
        {
            id: 'asset3',
            name: 'Database',
            description: 'Database asset'
        },
        {
            id: 'asset4',
            name: 'Server',
            description: 'Server asset'
        },
        {
            id: 'asset5',
            name: 'Network',
            description: 'Network asset'
        },
        {
            id: 'asset6',
            name: 'Application',
            description: 'Application asset'
        }
    ];

    //console.log('risks', risks);
    //console.log('assets', assets);
    const filteredRisks = risks && risks
        .filter(risk =>
            (risk.title || risk.name || '').toLowerCase().includes(searchRisk.toLowerCase()) ||
            (risk.definition || risk.description || '').toLowerCase().includes(searchRisk.toLowerCase())
        )
        .slice(0, 5);

   // //console.log('filteredRisks', filteredRisks);


    const filteredAssets = assets && assets
        .filter(asset =>
            (asset.name || asset.title || '').toLowerCase().includes(searchAsset.toLowerCase()) ||
            (asset.category || asset.description || '').toLowerCase().includes(searchAsset.toLowerCase())
        )
        .slice(0, 5);

    const onDragStart = (event, nodeType) => {
        event.dataTransfer.setData('application/reactflow', nodeType);
        event.dataTransfer.effectAllowed = 'move';
    };

    // Click outside to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check Risk dropdown
            if (
                showRiskSelect &&
                riskDropdownRef.current &&
                !riskDropdownRef.current.contains(event.target) &&
                riskButtonRef.current &&
                !riskButtonRef.current.contains(event.target)
            ) {
                setShowRiskSelect(false);
                setSearchRisk('');
            }

            // Check Asset dropdown
            if (
                showAssetSelect &&
                assetDropdownRef.current &&
                !assetDropdownRef.current.contains(event.target) &&
                assetButtonRef.current &&
                !assetButtonRef.current.contains(event.target)
            ) {
                setShowAssetSelect(false);
                setSearchAsset('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showRiskSelect, showAssetSelect]);

    const handleNodeClick = (event, type) => {
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const panelRect = event.currentTarget.closest('.react-flow__panel').getBoundingClientRect();

        setSelectedPosition({
            x: rect.left - panelRect.left,
            y: rect.height + 5 // Add small gap between button and dropdown
        });

        if (type === 'risk') {
            setShowRiskSelect(!showRiskSelect);
            setShowAssetSelect(false);
            setSearchRisk('');
        } else if (type === 'asset') {
            setShowAssetSelect(!showAssetSelect);
            setShowRiskSelect(false);
            setSearchAsset('');
        }
    };

    const renderDropdown = (type, items, searchValue, setSearchValue) => (
        <div
            ref={type === 'risk' ? riskDropdownRef : assetDropdownRef}
            className="node-dropdown"
            style={{
                position: 'absolute',
                top: selectedPosition.y,
                left: 0,
                top: 20,
                background: 'white',
                border: '1px solid #ccc',
                borderRadius: '5px',
                padding: '10px',
                zIndex: 1000,
                minWidth: '200px',
                maxWidth: '300px',
                boxSizing: 'border-box'
            }}
        >
            <div style={{ position: 'relative', marginBottom: '8px' }}>
                <input
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={`Search ${type}...`}
                    style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        outline: 'none'
                    }}
                    autoFocus
                />
            </div>
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                {items && items?.length > 0 ? (
                    items.map((item) => (
                        <div
                            key={item.id}
                            onClick={() => {
                                const position = { x: 100, y: 100 };
                                const newNode = {
                                    id: `${type}_${Date.now()}`,
                                    type: `${type}Node`,
                                    position,
                                    data: {
                                        id: type === 'risk' ? item.risk_id : item.id,
                                        name: item.title || item.name,
                                        description: item.definition || item.description || item.category,
                                        setMenuItem: setMenuItem,
                                        setActiveRiskForWorkflow: setActiveRiskForWorkflow,
                                        setActiveAssetForWorkflow: setActiveAssetForWorkflow
                                    }
                                };
                                setNodes((nds) => nds.concat(newNode));
                                if (type === 'risk') {
                                    setShowRiskSelect(false);
                                } else {
                                    setShowAssetSelect(false);
                                }
                            }}
                            style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                ':hover': {
                                    background: '#f5f5f5'
                                }
                            }}
                        >
                            <div style={{ fontWeight: 'bold' }}>
                                {item.title || item.name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                                {item.definition || item.description || item.category}
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={{ padding: '8px 12px', color: '#666' }}>
                        No {type}s found
                    </div>
                )}
                {items && items?.length === 5 && (
                    <div style={{ padding: '8px 12px', color: '#666', fontSize: '12px', borderTop: '1px solid #eee' }}>
                        Showing first 5 results
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Panel position="top" style={{ 
            width: '80vw', 
            padding: '10px', 
            background: '#f0f0f2', 
            borderBottom: '1px solid #f0f0f2', 
            borderRadius: '10px',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10
        }}>
            <div style={{
                display: 'flex',
                gap: '20px',
                justifyContent: 'center',
                position: 'relative',
                margin: '0 auto',
                width: '100%'
            }}>
                {Object.entries(nodeTypes).map(([type, config]) => (
                    <div
                        key={type}
                        ref={type === 'risk' ? riskButtonRef : type === 'asset' ? assetButtonRef : null}
                        onDragStart={(e) => onDragStart(e, type)}
                        onClick={(e) => (type === 'risk' || type === 'asset') && handleNodeClick(e, type)}
                        draggable={!(type === 'risk' || type === 'asset')}
                        className="node-palette-item"
                        style={{
                            position: 'relative',
                            cursor: (type === 'risk' || type === 'asset') ? 'pointer' : 'grab',
                            padding: '10px',
                            borderRadius: '5px',
                            background: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '120px',
                            height: '60px',
                            fontSize: '14px',
                            fontWeight: '500',
                            ...(type === 'yes' && {
                                background: '#47D45A',
                            }),
                            ...(type === 'no' && {
                                background: '#F82501',
                                color: 'black',
                            }),
                            ...(type === 'end' && {
                                background: '#176082',
                                color: 'white',
                                borderRadius: '8px',
                            }),
                            ...(type === 'risk' && {
                                background: '#fff',
                                borderColor: '#e74c3c',
                            }),
                            ...(type === 'asset' && {
                                background: '#fff',
                                borderColor: '#27ae60',
                            })
                        }}
                    >
                        <span>
                            {config.label}
                        </span>
                        {(type === 'risk' || type === 'asset') && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                zIndex: 1000
                            }}>
                                {type === 'risk' && showRiskSelect && renderDropdown('risk', filteredRisks, searchRisk, setSearchRisk)}
                                {type === 'asset' && showAssetSelect && renderDropdown('asset', filteredAssets, searchAsset, setSearchAsset)}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </Panel>
    );
}

// Separate Flow component to use the hooks
function Flow(props) {
    const { department_id, setMenuItem, setActiveRiskForWorkflow, setActiveAssetForWorkflow } = props;




    const dispatch = useDispatch();

    //it could be that the flowcharts are not loaded yet
    const flowcharts = useSelector((state) => state.workflows.flowcharts);
    const loading = useSelector((state) => state.workflows.loading);
    const error = useSelector((state) => state.workflows.error);

    useEffect(() => {
        dispatch(getFlowchartsNew(department_id));
    }, [dispatch, department_id]);


    //generate an id for the flowchart if none exists 
    const flowchartId = flowcharts?.length > 0 ? flowcharts[0].id :null;
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const { project } = useReactFlow();

    // Add loading state
    const [isSaving, setIsSaving] = useState(false);

    // Modify the save callback
    const onSave = useCallback(() => {
       /* if (!flowchartId) {
            console.error('No flowchart ID available');
            return;
        }
*/
        const diagramState = {
            id: flowchartId || null,
            content: JSON.stringify({
                nodes: nodes || [],
                edges: edges || [],
                timestamp: new Date().toISOString()
            })
        };

        setIsSaving(true); // Start loading
        try {
            dispatch(createorupdateFlowchart(diagramState, department_id))
                .then(() => {
                    setIsSaving(false); // Stop loading on success
                })
                .catch((error) => {
                    console.error('Error saving diagram:', error);
                    setIsSaving(false); // Stop loading on error
                });
        } catch (error) {
            console.error('Error saving diagram:', error);
            setIsSaving(false); // Stop loading on error
        }
    }, [nodes, edges, flowchartId, department_id, dispatch]);

    useEffect(() => {
        if (flowcharts?.length > 0) {
            try {
                const content = flowcharts[0]?.content;
                if (!content) {
                    //console.log('No content found in flowchart');
                    return;
                }

                const firstParse = JSON.parse(content);
                if (!firstParse?.content) {
                    //console.log('No inner content found');
                    return;
                }

                const parsedContent = JSON.parse(firstParse.content);
                if (!parsedContent) {
                    //console.log('Could not parse inner content');
                    return;
                }

                const newNodes = (parsedContent.nodes || []).map(node => {
                    // Base node data
                    const baseData = {
                        ...node.data,
                        setMenuItem: setMenuItem,
                        setActiveRiskForWorkflow: setActiveRiskForWorkflow,
                        setActiveAssetForWorkflow: setActiveAssetForWorkflow
                    };

                    // Add editing functions for editable text nodes
                    if (node.type === 'editableText' || node.type === 'editableVerticalText' || node.type === 'rhombusNode') {
                        return {
                            ...node,
                            data: {
                                ...baseData,
                                setIsEditing: (value) => {
                                    setNodes((nds) =>
                                        nds.map((n) =>
                                            n.id === node.id
                                                ? { ...n, data: { ...n.data, isEditing: value } }
                                                : n
                                        )
                                    );
                                },
                                onChange: (newText) => {
                                    setNodes((nds) =>
                                        nds.map((n) =>
                                            n.id === node.id
                                                ? { ...n, data: { ...n.data, text: newText } }
                                                : n
                                        )
                                    );
                                }
                            }
                        };
                    }

                    // Return regular node
                    return {
                        ...node,
                        data: baseData
                    };
                });

                const newEdges = parsedContent.edges || [];

                setNodes(newNodes);
                setEdges(newEdges);
                
            } catch (error) {
                console.error('Error loading flowchart:', error);
            }
        }
        else {
            //console.log('No flowcharts found');
            setNodes([]);
            setEdges([]);
        }
    }, [flowcharts, department_id, setMenuItem, setActiveRiskForWorkflow, setActiveAssetForWorkflow, setNodes]);

    

    const onConnect = useCallback(
        (params) => {
            const sourceNode = nodes.find(node => node.id === params.source);
            const targetNode = nodes.find(node => node.id === params.target);
            const isNoNodeConnection = sourceNode?.data?.content === 'NO' || targetNode?.data?.content === 'NO' ||
                sourceNode?.className === 'no-node' || targetNode?.className === 'no-node';

            const edgeColor = isNoNodeConnection ? '#F82501' : '#176082';

            setEdges((eds) =>
                addEdge(
                    {
                        ...params,
                        type: 'default',
                        animated: false,
                        style: {
                            stroke: edgeColor,
                            strokeWidth: 2
                        },
                        markerEnd: {
                            type: 'arrowclosed',
                            width: 20,
                            height: 20,
                            color: edgeColor
                        }
                    },
                    eds
                )
            );
        },
        [nodes, setEdges]
    );

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();

            const type = event.dataTransfer.getData('application/reactflow');
            if (!type) return;

            // Get the current bounds of the ReactFlow viewport
            const reactFlowBounds = event.currentTarget.getBoundingClientRect();

            // Get the position where the element was dropped
            const position = project({
                x: event.clientX - reactFlowBounds.left,
                y: event.clientY - reactFlowBounds.top,
            });

            let newNode = {
                id: `${type}_${Date.now()}`,
                position,
                className: nodeTypes[type].className,
            };

            // Handle different node types
            if (type === 'text' || type === 'verticalText') {
                // Editable text nodes
                newNode = {
                    ...newNode,
                    type: type === 'text' ? 'editableText' : 'editableVerticalText',
                    data: {
                        text: nodeTypes[type].content,
                        isEditing: false,
                        setIsEditing: (value) => {
                            setNodes((nds) =>
                                nds.map((node) =>
                                    node.id === newNode.id
                                        ? { ...node, data: { ...node.data, isEditing: value } }
                                        : node
                                )
                            );
                        },
                        onChange: (newText) => {
                            setNodes((nds) =>
                                nds.map((node) =>
                                    node.id === newNode.id
                                        ? { ...node, data: { ...node.data, text: newText } }
                                        : node
                                )
                            );
                        }
                    }
                };
            } else if (type === 'rhombus') {
                // Rhombus node
                newNode = {
                    ...newNode,
                    type: 'rhombusNode',
                    data: {
                        text: nodeTypes[type].content,
                        isEditing: false,
                        setIsEditing: (value) => {
                            setNodes((nds) =>
                                nds.map((node) =>
                                    node.id === newNode.id
                                        ? { ...node, data: { ...node.data, isEditing: value } }
                                        : node
                                )
                            );
                        },
                        onChange: (newText) => {
                            setNodes((nds) =>
                                nds.map((node) =>
                                    node.id === newNode.id
                                        ? { ...node, data: { ...node.data, text: newText } }
                                        : node
                                )
                            );
                        }
                    }
                };
            } else {
                // Regular nodes
                newNode = {
                    ...newNode,
                    type: 'default',
                    data: {
                        label: nodeTypes[type].content
                    }
                };
            }

            setNodes((nds) => nds.concat(newNode));
        },
        [setNodes, project]
    );

    return (
        <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDragOver={onDragOver}
            onDrop={onDrop}
            nodeTypes={customNodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            minZoom={0.5}
            maxZoom={2}
            //defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        >
            <Background color="#ccc" gap={16} />
            <Controls />
            <NodePalette setNodes={setNodes} setMenuItem={setMenuItem} setActiveRiskForWorkflow={setActiveRiskForWorkflow} setActiveAssetForWorkflow={setActiveAssetForWorkflow} />

            {/* Save and Load buttons */}
            <Panel position="bottom-right" style={{ padding: '10px', gap: '10px', display: 'flex' }}>
                <button
                    onClick={onSave}
                    disabled={isSaving}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#176082',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        minWidth: '120px', // Add this to prevent button size change
                        opacity: isSaving ? 0.7 : 1 // Add this for visual feedback
                    }}
                >
                    {isSaving ? (
                        <>
                            <div className="spinner" 
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    border: '2px solid #ffffff',
                                    borderTop: '2px solid transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    marginRight: '8px'
                                }}
                            />
                            Saving...
                        </>
                    ) : (
                        'Save Workflow'
                    )}
                </button>
                {/*<button
          onClick={onLoad}
          style={{
            padding: '8px 16px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          📂 Load Diagram
        </button> */}
            </Panel>
        </ReactFlow>
    );
}

// Main App component
function Workflow(props) {
    const { department_id, setMenuItem, setActiveRiskForWorkflow, setActiveAssetForWorkflow } = props;









    return (
        <div style={{ width: '90vw', height: '80vh', background: 'white' }}>
            <ReactFlowProvider>
                <Flow department_id={department_id} setMenuItem={setMenuItem} setActiveRiskForWorkflow={setActiveRiskForWorkflow} setActiveAssetForWorkflow={setActiveAssetForWorkflow} />
            </ReactFlowProvider>
        </div>
    );
}

export default Workflow;
